/**
 * LanguageSwitcher — renders a flag + language name selector for the user.
 *
 * Props:
 *   onClose (optional) — called after a language is chosen (e.g. to close a Menu).
 *
 * Persistence strategy:
 *   - localStorage key 'nexuma_lang' is updated immediately (i18next detector reads it).
 *   - The user's saved 'language' field in user_settings is synced to the backend
 *     so the preference is preserved across devices / browsers.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import MenuItem      from '@mui/material/MenuItem';
import Menu          from '@mui/material/Menu';
import Box           from '@mui/material/Box';
import Typography    from '@mui/material/Typography';
import Divider       from '@mui/material/Divider';
import LanguageIcon  from '@mui/icons-material/Language';
import ListItemIcon  from '@mui/material/ListItemIcon';
import ListItemText  from '@mui/material/ListItemText';
import CheckIcon     from '@mui/icons-material/Check';

import { SUPPORTED_LANGUAGES, LANG_STORAGE_KEY } from '../../i18n';
import apiService from '../../services/apiServices';
import globals    from '../../services/globals';

// ─────────────────────────────────────────────────────────────────────────────

export default function LanguageSwitcher({ onClose }) {
  const { i18n, t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleOpen  = (e) => { e.stopPropagation(); setAnchorEl(e.currentTarget); };
  const handleClose = ()  => setAnchorEl(null);

  const handleSelect = async (lang) => {
    handleClose();
    if (onClose) onClose();

    if (lang === i18n.language) return;

    // 1. Update i18next (also writes to localStorage via detector)
    await i18n.changeLanguage(lang);
    localStorage.setItem(LANG_STORAGE_KEY, lang);

    // 2. Persist to backend (user_settings.language) — best-effort, non-blocking
    try {
      const user = globals().getLogedInUser();
      if (user?.data?.username) {
        await apiService().execute('saveUserSettings', {
          username: user.data.username,
          language: lang,
        });
      }
    } catch (_) {
      // non-critical — localStorage is the primary source
    }
  };

  const currentLang = SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language)
    || SUPPORTED_LANGUAGES[0];

  return (
    <>
      <MenuItem
        onClick={handleOpen}
        sx={{ gap: 1.5, py: 1.5, px: 2 }}
      >
        <LanguageIcon fontSize="small" color="action" />
        <Typography variant="body2" sx={{ flex: 1 }}>
          {t('menu.language')}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, ml: 1 }}>
          {currentLang.flagSrc
            ? <img src={currentLang.flagSrc} alt={currentLang.nativeName} style={{ width: 18, height: 13, objectFit: 'cover', borderRadius: 2, verticalAlign: 'middle', marginRight: 4 }} />
            : `${currentLang.flag} `
          }{currentLang.nativeName}
        </Typography>
      </MenuItem>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { minWidth: 170, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.12)' } }}
      >
        <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing="0.05em">
            {t('language.selectLanguage')}
          </Typography>
        </Box>
        <Divider sx={{ mb: 0.5 }} />
        {SUPPORTED_LANGUAGES.map((lang) => {
          const isActive = i18n.language === lang.code;
          return (
            <MenuItem
              key={lang.code}
              onClick={() => handleSelect(lang.code)}
              selected={isActive}
              sx={{ gap: 1, py: 1, px: 2, borderRadius: 1, mx: 0.5 }}
            >
              <ListItemIcon sx={{ minWidth: 28, fontSize: '1rem' }}>
                {lang.flagSrc
                  ? <img src={lang.flagSrc} alt={lang.nativeName} style={{ width: 22, height: 16, objectFit: 'cover', borderRadius: 2 }} />
                  : lang.flag
                }
              </ListItemIcon>
              <ListItemText
                primary={lang.nativeName}
                primaryTypographyProps={{ variant: 'body2', fontWeight: isActive ? 700 : 400 }}
              />
              {isActive && <CheckIcon sx={{ fontSize: 16, color: 'primary.main', ml: 1 }} />}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
